import React from 'react';

import styled, { css } from 'styled-components';

import { IconFont } from '@/components';

interface ExtraItem {
  /** 上方的标签 */
  label: string;
  /** 下方的描述浅灰色文字 */
  value: React.ReactNode;
  /** 冻结宽度
   * boolean: 设置为 true, 固定宽度 110px, false: 最小宽度 110px
   * number: 自定义固定宽度
   */
  frozenWidth?: number | boolean;
}
/**
 * 用于展示卷的选项
 * @param value - 主要的值 (显示在最上面)
 * @param label - 主要的标签 (显示在最下面)
 * @param iconfontName - 图标的名称
 * @param extraItems - 额外的选项 (每一项的展示同上)
 */
export interface VolumeOptionLabelProps {
  iconfontName?: string;
  value: string;
  label?: string;
  extraItems?: ExtraItem[];
  style?: React.CSSProperties;
  /** 主值最多展示行数，超出省略 */
  valueLineClamp?: number;
}
export function VolumeOptionLabel({ iconfontName, value, label, extraItems, style, valueLineClamp }: VolumeOptionLabelProps) {
  return (
    <OptionLabel style={style}>
      {iconfontName && (
        <OptionIcon>
          <IconFont name={iconfontName} style={{ fontSize: 36 }} />
        </OptionIcon>
      )}
      <TitleBox>
        <Title>{label || value}</Title>
        <ValueText $lineClamp={valueLineClamp}>{value}</ValueText>
      </TitleBox>
      {extraItems &&
        extraItems.map((item, idx) => (
          <ItemBox key={idx} $frozenWidth={item.frozenWidth}>
            <span>{item.value}</span>
            <p>{item.label}</p>
          </ItemBox>
        ))}
    </OptionLabel>
  );
}
/** ============================ 样式 ==================================== */
const OptionLabel = styled.div`
  min-height: 52px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OptionIcon = styled.div`
  display: flex;
  align-items: center;
  opacity: 0.45;
`;

const ItemBaseBox = styled.div`
  line-height: 1rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
  span {
    font-size: 14px;
    word-break: break-all;
    white-space: normal;
  }
  p {
    font-size: 12px;
    opacity: 0.5;
  }
`;

const TitleBox = styled(ItemBaseBox)`
  width: 100%;
  min-width: 0;
  flex: 1;
`;

const ValueText = styled.p<{ $lineClamp?: number }>`
  margin: 0;
  font-size: 12px;
  opacity: 0.5;
  word-break: break-all;
  ${({ $lineClamp }) =>
    $lineClamp
      ? css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${$lineClamp};
          overflow: hidden;
        `
      : css`
          white-space: normal;
        `}
`;

const ItemBox = styled(TitleBox)<{ $frozenWidth?: boolean | number }>`
  flex-shrink: 0;
  ${({ $frozenWidth }) => {
    if (typeof $frozenWidth === 'number') {
      return css`
        width: ${$frozenWidth}px;
      `;
    }
    return $frozenWidth
      ? css`
          width: 110px;
        `
      : css`
          min-width: 110px;
          max-width: max-content;
        `;
  }};
`;

const Title = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
